import React, { useCallback } from 'react';
import {
  FDAlert,
  FDBox,
  FDHeadlineText,
  FDLozenge,
  FDPrimaryActionButton,
  FDStack,
} from '@facet-wealth/design';
import PropTypes from 'prop-types';
import { Richtext } from 'Packages/FacetDesign/Components/Richtext';
import { TextWithTooltip } from 'Organisms/AuthenticationFactorList/AuthenticationFactorListItem/TextWithTooltip';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';
import { useAuthenticationFactorListContext } from 'Organisms/AuthenticationFactorList/AuthenticationFactorListContext';
import fr from 'facet-rest';
import useMobileLayout from 'Hooks/useMobileLayout';
import { dataAttrs, testAttr } from 'Helpers/Testing';
import { BorderedTransparentBox } from 'Packages/FacetDesign/Components/Experimental/BorderedTransparentBox';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import {
  AuthenticationFactorItems,
} from 'Organisms/AuthenticationFactorList/AuthenticationFactorItems/AuthenticationFactorItems';

export const AuthenticationFactorListItem = ({
  authenticationFactorResource,
  authFactorIncludedResources,
}) => {
  const isMobile = useMobileLayout();
  const { loadModalCollectionByLink } = useModalContext();
  const { setModalLink } = useAuthenticationFactorListContext();
  const { logStandardizedEvent } = useEventTrackingLogEvent();

  const TextWithTooltipWrapped = useCallback((props) => <TextWithTooltip resource={authenticationFactorResource} {...props} />, [authenticationFactorResource]);

  const handleUpdateAuthenticationFactor = (relationship) => {
    logStandardizedEvent(categories.userSettingsSecurity, eventTypes.clickButton, `Authentication Type ${relationship.meta('type')} Modify: Open change password modal`);
    setModalLink(relationship.link('related'));
  };

  const handleRegisterAuthenticationFactor = async (rel) => {
    logStandardizedEvent(
      categories.userSettingsSecurity,
      eventTypes.clickButton,
      'enable factor',
      {
        eventProperties: {
          authFactorType: authenticationFactorResource.attr('authenticationType'),
        },
      },
    );
    setModalLink(rel.link('related'));
    return null;
  };

  return (
    <BorderedTransparentBox padding={3}>
      <FDStack direction={isMobile ? 'column' : 'row'} justifyContent={'space-between'} alignItems={isMobile ? 'flex-start' : 'center'} spacing={2} flex={1} wrap>
        <FDStack direction={'column'} spacing={2}>
          <FDStack spacing={1} alignItems={'center'}>
            <FDHeadlineText size={'sm'} color={'textSecondary'} value={authenticationFactorResource.selectedOptionPrompt('authenticationType')} />
            {authenticationFactorResource.attr('isMostSecure') === 'true' ? <FDLozenge size={'lg'} color={'success'} label="Our Pick" /> : null}
            {authenticationFactorResource.attr('enabled') === 'true' ? <FDLozenge size={'lg'} color={'info'} label="Enabled" /> : null}
          </FDStack>
          <Richtext
            Component={TextWithTooltipWrapped}
            richtext
            value={authenticationFactorResource.attr('description')}
          />
          {authenticationFactorResource.attr('alertTitle') || authenticationFactorResource.attr('alertDescription')
            ? <FDAlert title={authenticationFactorResource.attr('alertTitle')} description={authenticationFactorResource.attr('alertDescription')} status={'warning'} />
            : null}
          {authenticationFactorResource.withRelationship(fr.relationships.authenticationFactorItems.name,
            (rel) => <AuthenticationFactorItems relationship={rel} authFactorIncludedResources={authFactorIncludedResources} />,
            () => null)}
          {authenticationFactorResource.withRelationship(fr.relationships.passkeyRegistration.name, (rel) => (
            <FDBox>
              <FDPrimaryActionButton
                label={rel.meta('prompt')}
                onClick={() => loadModalCollectionByLink(rel.link('related').url())}
              />
            </FDBox>
          ), () => null)}
        </FDStack>
        {authenticationFactorResource.withRelationship(fr.relationships.authenticationFactorModify.name,
          (rel) => (
            <FDBox>
              <FDPrimaryActionButton
                label={rel.meta('prompt')}
                onClick={() => handleUpdateAuthenticationFactor(rel)}
                {...dataAttrs('security-settings-modify-btn', authenticationFactorResource, {
                  [testAttr.dataType]: authenticationFactorResource.attr('authenticationType'),
                })}
                disabled={rel.meta('disabled') || false}
              />
            </FDBox>
          ),
          () => null)}
        {authenticationFactorResource.withRelationship(fr.relationships.authenticationFactorRegister.name,
          (rel) => (
            <FDBox>
              <FDPrimaryActionButton
                label={rel.meta('prompt')}
                onClick={() => handleRegisterAuthenticationFactor(rel)}
                {...dataAttrs('security-settings-register-btn', authenticationFactorResource, {
                  [testAttr.dataType]: authenticationFactorResource.attr('authenticationType'),
                })}
              />
            </FDBox>
          ), () => null)}
      </FDStack>
    </BorderedTransparentBox>
  );
};


AuthenticationFactorListItem.propTypes = {
  authenticationFactorResource: PropTypes.object.isRequired,
  authFactorIncludedResources: PropTypes.object.isRequired,
};
